/**
 * Mock auth service — wraps the in-memory user fixtures and records the mock
 * audit log (audit side-effects live here, not in AuthContext, so real mode
 * leaves auditing to the backend).
 */

import { mockLogin, mockValidateToken, getUserByEmail } from '@/data/mockUsers'
import { addAuditEntry } from '@/data/mockAuditLog'
import { getPermissionsForRole } from '@/data/mockPermissions'
import {
  ACCESS_CONTRACT_VERSION,
  normalizeRole,
  PLATFORM_ROLE_PERMISSIONS,
} from '@/security/accessContract'
import { mockDelay } from '@/services/factory'
import type { AuthLoginResult, AuthLogoutParams, AuthService } from './auth.service'
import type { AuthUser } from '@/types/auth.types'

const LOCAL_IP = '127.0.0.1'

function withEffectiveAccess(user: AuthUser): AuthUser {
  const role = normalizeRole(user.role)
  const platformPermissions =
    PLATFORM_ROLE_PERMISSIONS[user.role as keyof typeof PLATFORM_ROLE_PERMISSIONS]
  const permissions = platformPermissions
    ? [...platformPermissions]
    : getPermissionsForRole(user.role).map(permission => permission.permissionKey)
  const featureNames: Record<string, string> = {
    client: 'clients',
    document: 'documents',
    assessment: 'assessments',
    gst_assessment: 'gst_assessments',
    payment: 'payments',
    notification: 'notifications',
    reminder: 'reminders',
    insight: 'insights',
    reports: 'reports',
    settings: 'settings',
    session: 'sessions',
    audit: 'audit',
    user: 'users',
  }
  const features = [
    ...new Set(
      permissions
        .map(permission => {
          const domain = permission.split('.')[1]
          return featureNames[domain] ?? domain
        })
        .filter(Boolean)
    ),
  ]

  return {
    ...user,
    role,
    roles: [role],
    permissions,
    features,
    requiresRoleClassification: role === 'staff' || role === 'client',
    accessContractVersion: ACCESS_CONTRACT_VERSION,
  }
}

export function createMockAuthService(): AuthService {
  return {
    async login(email, password, _mfaCode, _rememberMe): Promise<AuthLoginResult> {
      await mockDelay(500)
      const result = mockLogin(email, password)

      if (!result) {
        addAuditEntry({
          userEmail: email,
          action: 'login_failed',
          resourceType: 'session',
          details: {
            loginMethod: 'password',
            reason: 'Invalid credentials',
            attemptedEmail: email,
          },
          ipAddress: LOCAL_IP,
          success: false,
          errorMessage: 'Invalid email or password',
        })
        throw new Error('Invalid email or password')
      }

      const { user: fixtureUser, token, refreshToken, sessionId } = result
      const user = withEffectiveAccess(fixtureUser)

      addAuditEntry({
        userId: user.id,
        userEmail: user.email,
        performedByName: user.fullName,
        action: 'login',
        resourceType: 'session',
        details: { loginMethod: 'password', sessionId, tenantId: user.tenantId },
        ipAddress: LOCAL_IP,
        success: true,
      })

      return { user, token, refreshToken, sessionId }
    },

    async validateToken(token): Promise<AuthUser | null> {
      const user = mockValidateToken(token)
      return user ? withEffectiveAccess(user) : null
    },

    async logout({ user, sessionId, allDevices }: AuthLogoutParams): Promise<void> {
      if (user) {
        addAuditEntry({
          userId: user.id,
          userEmail: user.email,
          performedByName: user.fullName,
          action: allDevices ? 'all_sessions_terminated' : 'logout',
          resourceType: 'session',
          details: { sessionId: sessionId ?? null },
          ipAddress: LOCAL_IP,
          success: true,
        })
      }
      await mockDelay(200)
    },

    async refresh(refreshToken): Promise<{ token: string; refreshToken: string }> {
      await mockDelay(300)
      // Preserve the `mock-token-{userId}-{ts}` shape so a subsequent
      // validateToken still resolves the user. Source the userId from the
      // incoming `mock-refresh-{userId}-{ts}` token.
      const parts = refreshToken.split('-')
      const userId = parts.length >= 4 ? parts.slice(2, -1).join('-') : 'unknown'
      const now = Date.now()
      return {
        token: `mock-token-${userId}-${now}`,
        refreshToken: `mock-refresh-${userId}-${now}`,
      }
    },

    async forgotPassword(email): Promise<{ resetToken?: string }> {
      await mockDelay(500)
      const user = getUserByEmail(email)

      // Enumeration-safe: the same result is returned whether or not the email
      // matched. We still record the (success/failure) audit entry, mirroring
      // the prior page behaviour.
      if (user) {
        addAuditEntry({
          userId: user.userId,
          userEmail: user.email,
          performedByName: user.fullName,
          action: 'password_reset_requested',
          resourceType: 'user',
          resourceId: user.userId,
          details: { email: user.email, method: 'email' },
          ipAddress: LOCAL_IP,
          success: true,
        })
      } else {
        addAuditEntry({
          userEmail: email,
          action: 'password_reset_requested',
          resourceType: 'user',
          details: { email, method: 'email', reason: 'Email not found' },
          ipAddress: LOCAL_IP,
          success: false,
        })
      }

      // A `demo-`-prefixed token keeps the reset link demoable end-to-end.
      return { resetToken: `demo-reset-${Date.now()}` }
    },

    async resetPassword(token, _newPassword): Promise<void> {
      await mockDelay(800)
      // Mirror the prior mock behaviour: any `demo-` token is accepted.
      if (!token || !token.startsWith('demo-')) {
        throw new Error('Invalid or expired reset link')
      }
      addAuditEntry({
        action: 'password_changed',
        resourceType: 'user',
        details: { method: 'reset_token', tokenUsed: true },
        ipAddress: LOCAL_IP,
        success: true,
      })
    },
  }
}
